/**
 * Chat Context Provider
 *
 * Provides chat state management and WebSocket real-time messaging functionality.
 *
 * State:
 * - channels: Array of available channels (filtered by user access)
 * - activeChannel: Currently selected channel or DM conversation
 * - messages: Map of channelId/conversationId -> messages array
 * - unreadCounts: Map of channelId -> unread message count
 * - agentStatuses: Map of agentId -> operational status
 *
 * Features:
 * - WebSocket connection with automatic reconnection
 * - Real-time message receiving and broadcasting
 * - Channel subscription management
 * - Unread count tracking with mark-as-read
 * - Agent status updates
 * - DM conversation support
 *
 * @module contexts/ChatContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';

// ============================================
// Constants
// ============================================

const WS_RECONNECT_DELAY_MS = 1000;
const WS_MAX_RECONNECT_DELAY_MS = 30000;
const WS_PING_INTERVAL_MS = 30000;
const WS_CONNECTION_TIMEOUT_MS = 10000;
const MESSAGE_PAGE_SIZE = 50;

const WS_MESSAGE_TYPES = {
  // Client -> Server
  SUBSCRIBE: 'subscribe',
  UNSUBSCRIBE: 'unsubscribe',
  SEND_MESSAGE: 'send_message',
  SEND_DM: 'send_dm',
  MARK_READ: 'mark_read',
  QUEUE_NEXT: 'queue_next',
  STATUS_UPDATE: 'status_update',
  PING: 'ping',

  // Server -> Client
  SUBSCRIBED: 'subscribed',
  UNSUBSCRIBED: 'unsubscribed',
  MESSAGE: 'message',
  DM: 'dm',
  MESSAGE_SENT: 'message_sent',
  DM_SENT: 'dm_sent',
  MARKED_READ: 'marked_read',
  QUEUE_PROCESSED: 'queue_processed',
  QUEUE_NOTIFICATION: 'queue_notification',
  AGENT_STATUS: 'agent_status',
  AGENT_GENERATING: 'agent_generating',
  STATUS_UPDATED: 'status_updated',
  PRESENCE: 'presence',
  ERROR: 'error',
  PONG: 'pong'
};

const CONNECTION_STATE = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting'
};

// ============================================
// Context
// ============================================

const ChatContext = createContext(null);

// ============================================
// Helper Functions
// ============================================

/**
 * Generate a unique request ID for WebSocket messages
 * @returns {string} Unique request ID
 */
function generateRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Build the WebSocket URL based on current location
 * @returns {string} WebSocket URL
 */
function getWebSocketUrl() {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;

  // Determine backend host:
  // - Local dev (localhost:5173) → direct connect to backend on :3001
  // - Remote dev (dev.agents.sneakercloud.de) → use Vite proxy (same host)
  // - Production → use nginx proxy (same host)
  let backendHost;
  if (import.meta.env.DEV && host.includes('localhost')) {
    // Local development without proxy
    backendHost = host.replace(/:\d+$/, ':3001');
  } else {
    // Use current host - Vite or nginx will proxy to backend
    backendHost = host;
  }
  return `${protocol}//${backendHost}/ws`;
}

/**
 * Get DM conversation key for a pair of users/agents
 * @param {string} userId - Current user ID
 * @param {string} recipientId - Recipient ID
 * @returns {string} Conversation key
 */
function getDmConversationKey(userId, recipientId) {
  const ids = [userId, recipientId].sort();
  return `dm:${ids[0]}:${ids[1]}`;
}

// ============================================
// Provider Component
// ============================================

/**
 * ChatProvider Component
 *
 * Provides chat functionality to the application via React Context.
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} [props.userId] - Current user ID (null if not authenticated)
 * @param {string} [props.agentId] - Current agent ID (for agent connections)
 * @param {string} [props.agentUuid] - Current agent UUID (for agent connections)
 * @returns {React.ReactElement} Provider component
 */
export function ChatProvider({ children, userId, agentId, agentUuid }) {
  // ----------------------------------------
  // State
  // ----------------------------------------

  // Channel list
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState(null);

  // Active channel/conversation
  const [activeChannel, setActiveChannel] = useState(null);
  const [activeDmRecipient, setActiveDmRecipient] = useState(null);

  // Messages - Map<channelId|conversationKey, Message[]>
  const [messages, setMessages] = useState(new Map());
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesHasMore, setMessagesHasMore] = useState(new Map());

  // Unread counts - Map<channelId, number>
  const [unreadCounts, setUnreadCounts] = useState(new Map());

  // DM unread counts - Map<conversationKey, number>
  const [dmUnreadCounts, setDmUnreadCounts] = useState(new Map());

  // Agent statuses - Map<agentId, {operationalStatus, currentProject}>
  const [agentStatuses, setAgentStatuses] = useState(new Map());

  // Generating agents - Set<agentId> - agents currently generating responses
  const [generatingAgents, setGeneratingAgents] = useState(new Set());

  // All agents (for mentions)
  const [agents, setAgents] = useState([]);

  // DM contacts - { agents: [], users: [], recent: [] }
  const [dmContacts, setDmContacts] = useState({ agents: [], users: [], recent: [] });
  const [dmContactsLoading, setDmContactsLoading] = useState(false);

  // Channel members - Map<channelId, { users: [], agents: [] }>
  const [channelMembers, setChannelMembers] = useState(new Map());

  // WebSocket connection state
  const [connectionState, setConnectionState] = useState(CONNECTION_STATE.DISCONNECTED);
  const [connectionError, setConnectionError] = useState(null);

  // Subscribed channels set
  const [subscribedChannels, setSubscribedChannels] = useState(new Set());

  // ----------------------------------------
  // Refs
  // ----------------------------------------

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pendingRequests = useRef(new Map());
  const isComponentMounted = useRef(true);
  const messagesRef = useRef(messages);
  // IMPORTANT: subscribedChannelsRef prevents stale closure bug in WebSocket onopen.
  // Without this ref, reconnect would use outdated channel list from frozen closure.
  // See connect() onopen handler where subscribedChannelsRef.current is used.
  const subscribedChannelsRef = useRef(subscribedChannels);
  // Track last received message timestamp for missed-message recovery on reconnect
  const lastMessageTimestampRef = useRef(null);

  // Keep refs in sync with state (read on every render)
  messagesRef.current = messages;
  subscribedChannelsRef.current = subscribedChannels;

  // ----------------------------------------
  // WebSocket Message Handling
  // ----------------------------------------

  /**
   * Handle incoming WebSocket message
   * @param {MessageEvent} event - WebSocket message event
   */
  const handleWsMessage = useCallback((event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    // Handle pending request responses
    if (data.requestId && pendingRequests.current.has(data.requestId)) {
      const { resolve, reject } = pendingRequests.current.get(data.requestId);
      pendingRequests.current.delete(data.requestId);

      if (data.type === WS_MESSAGE_TYPES.ERROR) {
        reject(new Error(data.message || 'Unknown error'));
      } else {
        resolve(data);
      }
    }

    // Handle message types
    console.log('[ChatContext] WebSocket message received:', { type: data.type, hasChannelId: !!data.channelId });
    switch (data.type) {
      case WS_MESSAGE_TYPES.MESSAGE:
        console.log('[ChatContext] Routing to handleIncomingMessage');
        handleIncomingMessage(data);
        break;

      case WS_MESSAGE_TYPES.DM:
        handleIncomingDm(data);
        break;

      case WS_MESSAGE_TYPES.SUBSCRIBED:
        handleSubscribed(data);
        break;

      case WS_MESSAGE_TYPES.UNSUBSCRIBED:
        handleUnsubscribed(data);
        break;

      case WS_MESSAGE_TYPES.AGENT_STATUS:
        handleAgentStatus(data);
        break;

      case WS_MESSAGE_TYPES.AGENT_GENERATING:
        handleAgentGenerating(data);
        break;

      case WS_MESSAGE_TYPES.QUEUE_NOTIFICATION:
        handleQueueNotification(data);
        break;

      case WS_MESSAGE_TYPES.MARKED_READ:
        handleMarkedRead(data);
        break;

      case WS_MESSAGE_TYPES.PRESENCE:
        handlePresence(data);
        break;

      case WS_MESSAGE_TYPES.PONG:
        // Pong received, connection is alive
        break;

      case WS_MESSAGE_TYPES.ERROR:
        handleError(data);
        break;

      case 'missed_messages_complete':
        // Missed messages recovery complete - log for debugging
        console.log('[ChatContext] Missed messages recovery complete:', {
          channelId: data.channelId,
          count: data.count,
          hasMore: data.hasMore
        });
        break;

      default:
        // Unknown message type, ignore
        break;
    }
  }, []);

  /**
   * Handle incoming channel message
   * @param {object} data - Message data
   */
  const handleIncomingMessage = useCallback((data) => {
    const { channelId, messageId, senderId, senderType, senderName, senderAvatarUrl, content, replyToId, createdAt, mentionedUserIds, mentionedAgentIds, attachments } = data;

    // CRITICAL: Validate channelId to prevent storing messages under wrong key
    if (!channelId) {
      console.error('[ChatContext] Received message without channelId, ignoring:', { messageId, content: content?.substring(0, 50) });
      return;
    }

    // Debug: Log full message data for channel-bug investigation
    console.log('[ChatContext] handleIncomingMessage:', {
      channelId,
      messageId,
      senderName,
      content: content?.substring(0, 100),
      activeChannelId: activeChannel?.id,
      activeChannelName: activeChannel?.name
    });

    const msgCreatedAt = createdAt || new Date().toISOString();
    const message = {
      id: messageId,
      channelId,
      senderId,
      senderType,
      senderName,
      senderAvatarUrl: senderAvatarUrl || null,
      content,
      replyToId: replyToId || null,
      mentionedUserIds: mentionedUserIds || [],
      mentionedAgentIds: mentionedAgentIds || [],
      attachments: attachments || [],
      createdAt: msgCreatedAt
    };

    // Track last message timestamp for reconnect recovery
    lastMessageTimestampRef.current = msgCreatedAt;

    // Add message to channel
    setMessages((prev) => {
      const newMap = new Map(prev);
      const channelMessages = newMap.get(channelId) || [];

      // Prevent duplicates
      if (channelMessages.some((m) => m.id === message.id)) {
        console.log('[ChatContext] Duplicate message detected, skipping:', messageId);
        return prev;
      }

      console.log('[ChatContext] Adding message to channel:', { channelId, messageCount: channelMessages.length + 1 });
      newMap.set(channelId, [...channelMessages, message]);
      return newMap;
    });

    // Update unread count if not active channel
    if (activeChannel?.id !== channelId) {
      setUnreadCounts((prev) => {
        const newMap = new Map(prev);
        newMap.set(channelId, (prev.get(channelId) || 0) + 1);
        return newMap;
      });
    }
  }, [activeChannel?.id, activeChannel?.name]);

  /**
   * Handle incoming DM
   * @param {object} data - DM data
   */
  const handleIncomingDm = useCallback((data) => {
    // Support both 'id' and 'messageId' field names
    const { id, messageId, senderId, senderType, senderName, senderAvatarUrl, content, replyToId, createdAt, attachments } = data;
    const msgId = messageId || id;

    console.log('[ChatContext] handleIncomingDm called:', { msgId, senderId, senderName, content: content?.substring(0, 50), attachmentCount: attachments?.length || 0 });

    const currentId = userId || agentId;
    if (!currentId) return;

    const conversationKey = getDmConversationKey(currentId, senderId);
    console.log('[ChatContext] DM conversation key:', conversationKey, 'currentId:', currentId, 'senderId:', senderId);

    const message = {
      id: msgId,
      senderId,
      senderType,
      senderName,
      senderAvatarUrl: senderAvatarUrl || null,
      content,
      replyToId: replyToId || null,
      attachments: attachments || [],
      isDm: true,
      createdAt: createdAt || new Date().toISOString()
    };

    // Add message to conversation
    setMessages((prev) => {
      const newMap = new Map(prev);
      const conversationMessages = newMap.get(conversationKey) || [];

      // Prevent duplicates
      if (conversationMessages.some((m) => m.id === message.id)) {
        return prev;
      }

      newMap.set(conversationKey, [...conversationMessages, message]);
      return newMap;
    });

    // Update DM unread count if not active conversation
    if (activeDmRecipient?.id !== senderId) {
      setDmUnreadCounts((prev) => {
        const newMap = new Map(prev);
        newMap.set(conversationKey, (prev.get(conversationKey) || 0) + 1);
        return newMap;
      });
    }
  }, [userId, agentId, activeDmRecipient?.id]);

  /**
   * Handle subscribed confirmation
   * @param {object} data - Subscription data
   */
  const handleSubscribed = useCallback((data) => {
    const { channelId, channelName } = data;

    setSubscribedChannels((prev) => {
      const newSet = new Set(prev);
      newSet.add(channelId);
      return newSet;
    });

    // Update channel in list if needed
    setChannels((prev) =>
      prev.map((ch) =>
        ch.id === channelId ? { ...ch, name: channelName } : ch
      )
    );
  }, []);

  /**
   * Handle unsubscribed confirmation
   * @param {object} data - Unsubscription data
   */
  const handleUnsubscribed = useCallback((data) => {
    const { channelId } = data;

    setSubscribedChannels((prev) => {
      const newSet = new Set(prev);
      newSet.delete(channelId);
      return newSet;
    });
  }, []);

  /**
   * Handle agent status update
   * @param {object} data - Status data
   */
  const handleAgentStatus = useCallback((data) => {
    const { agentId: statusAgentId, agentName, operationalStatus, currentProject } = data;

    setAgentStatuses((prev) => {
      const newMap = new Map(prev);
      newMap.set(statusAgentId, {
        agentId: statusAgentId,
        agentName,
        operationalStatus,
        currentProject: currentProject || null
      });
      return newMap;
    });
  }, []);

  // Track pending "stop generating" timeouts to debounce rapid on/off
  const generatingTimeouts = useRef(new Map());

  /**
   * Handle agent generating status update
   * Debounces the "stop generating" to ensure badge is visible for at least 1.5s
   * @param {object} data - Generating data { agentId, isGenerating }
   */
  const handleAgentGenerating = useCallback((data) => {
    const { agentId: generatingAgentId, isGenerating } = data;

    if (isGenerating) {
      // Clear any pending "stop" timeout for this agent
      const existingTimeout = generatingTimeouts.current.get(generatingAgentId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        generatingTimeouts.current.delete(generatingAgentId);
      }

      // Immediately show as generating
      setGeneratingAgents((prev) => {
        const newSet = new Set(prev);
        newSet.add(generatingAgentId);
        return newSet;
      });
    } else {
      // Debounce the "stop generating" - keep badge visible for at least 1.5s
      const existingTimeout = generatingTimeouts.current.get(generatingAgentId);
      if (!existingTimeout) {
        const timeout = setTimeout(() => {
          setGeneratingAgents((prev) => {
            const newSet = new Set(prev);
            newSet.delete(generatingAgentId);
            return newSet;
          });
          generatingTimeouts.current.delete(generatingAgentId);
        }, 1500); // 1.5 second minimum display time
        generatingTimeouts.current.set(generatingAgentId, timeout);
      }
    }
  }, []);

  /**
   * Handle queue notification (for agents)
   * @param {object} data - Queue data
   */
  const handleQueueNotification = useCallback((data) => {
    // Queue notifications are primarily for agent clients
    // Could be used to show a badge or notification
  }, []);

  /**
   * Handle marked read confirmation
   * @param {object} data - Marked read data
   */
  const handleMarkedRead = useCallback((data) => {
    const { channelId } = data;

    setUnreadCounts((prev) => {
      const newMap = new Map(prev);
      newMap.set(channelId, 0);
      return newMap;
    });
  }, []);

  /**
   * Handle presence update
   * @param {object} data - Presence data
   */
  const handlePresence = useCallback((data) => {
    const { agentId: presenceAgentId, isOnline, operationalStatus } = data;

    if (presenceAgentId) {
      setAgentStatuses((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(presenceAgentId) || {};
        newMap.set(presenceAgentId, {
          ...existing,
          agentId: presenceAgentId,
          isOnline,
          operationalStatus: operationalStatus || existing.operationalStatus || 'offline'
        });
        return newMap;
      });
    }
  }, []);

  /**
   * Handle error message
   * @param {object} data - Error data
   */
  const handleError = useCallback((data) => {
    setConnectionError({
      code: data.code,
      message: data.message,
      details: data.details
    });
  }, []);

  // ----------------------------------------
  // WebSocket Connection Management
  // ----------------------------------------

  /**
   * Send a WebSocket message
   * @param {object} message - Message to send
   * @returns {Promise<object>} Response from server
   */
  const sendWsMessage = useCallback((message) => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const requestId = generateRequestId();
      const messageWithId = { ...message, requestId };

      // Store pending request
      const timeout = setTimeout(() => {
        pendingRequests.current.delete(requestId);
        reject(new Error('Request timeout'));
      }, WS_CONNECTION_TIMEOUT_MS);

      pendingRequests.current.set(requestId, {
        resolve: (data) => {
          clearTimeout(timeout);
          resolve(data);
        },
        reject: (error) => {
          clearTimeout(timeout);
          reject(error);
        }
      });

      try {
        const jsonMessage = JSON.stringify(messageWithId);
        console.log('[sendWsMessage] Sending:', jsonMessage);
        wsRef.current.send(jsonMessage);
      } catch (error) {
        pendingRequests.current.delete(requestId);
        clearTimeout(timeout);
        reject(error);
      }
    });
  }, []);

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    setConnectionState(
      reconnectAttempts.current > 0
        ? CONNECTION_STATE.RECONNECTING
        : CONNECTION_STATE.CONNECTING
    );

    try {
      const ws = new WebSocket(getWebSocketUrl());

      ws.onopen = () => {
        if (!isComponentMounted.current) {
          ws.close();
          return;
        }

        setConnectionState(CONNECTION_STATE.CONNECTED);
        setConnectionError(null);
        reconnectAttempts.current = 0;

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: WS_MESSAGE_TYPES.PING }));
          }
        }, WS_PING_INTERVAL_MS);

        // Resubscribe to channels with since parameter for missed message recovery
        const sinceTimestamp = lastMessageTimestampRef.current;
        if (sinceTimestamp) {
          console.log('[ChatContext] Reconnecting with missed-message recovery since:', sinceTimestamp);
        }

        subscribedChannelsRef.current.forEach((channelId) => {
          const subscribeMsg = {
            type: WS_MESSAGE_TYPES.SUBSCRIBE,
            channelId
          };
          // Include since parameter for missed-message recovery on reconnect
          if (sinceTimestamp) {
            subscribeMsg.since = sinceTimestamp;
          }
          ws.send(JSON.stringify(subscribeMsg));
        });
      };

      ws.onmessage = handleWsMessage;

      ws.onerror = () => {
        setConnectionError({ message: 'WebSocket connection error' });
      };

      ws.onclose = () => {
        if (!isComponentMounted.current) {
          return;
        }

        setConnectionState(CONNECTION_STATE.DISCONNECTED);

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Attempt reconnection with exponential backoff
        reconnectAttempts.current += 1;
        const delay = Math.min(
          WS_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts.current - 1),
          WS_MAX_RECONNECT_DELAY_MS
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isComponentMounted.current) {
            connect();
          }
        }, delay);
      };

      wsRef.current = ws;
    } catch {
      setConnectionState(CONNECTION_STATE.DISCONNECTED);
      setConnectionError({ message: 'Failed to create WebSocket connection' });
    }
  }, [handleWsMessage]);

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionState(CONNECTION_STATE.DISCONNECTED);
  }, []);

  // ----------------------------------------
  // Channel Actions
  // ----------------------------------------

  /**
   * Fetch available channels
   */
  const fetchChannels = useCallback(async () => {
    setChannelsLoading(true);
    setChannelsError(null);

    try {
      const response = await fetch('/api/channels');

      if (!response.ok) {
        throw new Error(`Failed to fetch channels: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { data: [...], pagination: {...} }
      setChannels(data.data || data.channels || data || []);
    } catch (error) {
      setChannelsError(error.message);
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  /**
   * Fetch available agents (for mentions)
   * Includes disabled agents so mentions work even for inactive agents
   */
  const fetchAgents = useCallback(async () => {
    try {
      // Include disabled agents so @mentions work for all agents
      const response = await fetch('/api/agents?includeDisabled=true&limit=100');

      if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { data: [...], pagination: {...} }
      setAgents(data.data || data.agents || data || []);
    } catch (error) {
      console.error('Failed to fetch agents for mentions:', error);
    }
  }, []);

  /**
   * Fetch DM contacts (users and agents available for direct messaging)
   */
  const fetchDmContacts = useCallback(async () => {
    setDmContactsLoading(true);
    try {
      const response = await fetch('/api/dm/contacts', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch DM contacts: ${response.statusText}`);
      }

      const data = await response.json();
      setDmContacts({
        agents: data.agents || [],
        users: data.users || [],
        recent: data.recent || []
      });
    } catch (error) {
      console.error('Failed to fetch DM contacts:', error);
    } finally {
      setDmContactsLoading(false);
    }
  }, []);

  /**
   * Create a new channel
   * @param {object} channelData - Channel data (name, displayName, description, type, projectId, requiredRoles)
   * @returns {Promise<object>} Created channel
   */
  const createChannel = useCallback(async (channelData) => {
    const response = await fetch('/api/channels', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(channelData)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to create channel: ${response.statusText}`);
    }

    const data = await response.json();
    const newChannel = data.data || data;

    // Refresh channel list to include new channel
    await fetchChannels();

    return newChannel;
  }, [fetchChannels]);

  // Ref to track which channels are being fetched (prevents duplicate requests)
  const fetchingMembersRef = useRef(new Set());

  /**
   * Fetch members of a channel
   * @param {string} channelId - Channel ID to fetch members for
   * @returns {Promise<object>} Members object { users: [], agents: [] }
   */
  const fetchChannelMembers = useCallback(async (channelId) => {
    // Check if already fetching this channel
    if (fetchingMembersRef.current.has(channelId)) {
      return null;
    }

    // Mark as fetching
    fetchingMembersRef.current.add(channelId);

    try {
      const response = await fetch(`/api/channels/${channelId}/members`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch channel members: ${response.statusText}`);
      }

      const data = await response.json();
      const rawMembers = data.data || data;
      console.log('[ChatContext] fetchChannelMembers response:', { channelId, rawMembers });

      // Transform API response: array of {type, user, agent} -> {users: [], agents: []}
      const members = {
        users: [],
        agents: []
      };

      for (const member of rawMembers) {
        if (member.type === 'user' && member.user) {
          members.users.push(member.user);
        } else if (member.type === 'agent' && member.agent) {
          members.agents.push(member.agent);
        }
      }

      setChannelMembers(prev => new Map(prev).set(channelId, members));
      return members;
    } catch (error) {
      console.error('[ChatContext] Failed to fetch channel members:', error);
      // Store empty result so UI knows fetch was attempted
      const emptyMembers = { users: [], agents: [] };
      setChannelMembers(prev => new Map(prev).set(channelId, emptyMembers));
      return emptyMembers;
    } finally {
      // Remove from fetching set
      fetchingMembersRef.current.delete(channelId);
    }
  }, []);

  /**
   * Get cached channel members
   * @param {string} channelId - Channel ID
   * @returns {object|null} Members object or null if not loaded
   */
  const getChannelMembers = useCallback((channelId) => {
    return channelMembers.get(channelId) || null;
  }, [channelMembers]);

  /**
   * Subscribe to a channel
   * @param {string} channelId - Channel ID to subscribe to
   * @returns {Promise<object>} Subscription result
   */
  const subscribeToChannel = useCallback(
    async (channelId) => {
      return sendWsMessage({
        type: WS_MESSAGE_TYPES.SUBSCRIBE,
        channelId
      });
    },
    [sendWsMessage]
  );

  /**
   * Unsubscribe from a channel
   * @param {string} channelId - Channel ID to unsubscribe from
   * @returns {Promise<object>} Unsubscription result
   */
  const unsubscribeFromChannel = useCallback(
    async (channelId) => {
      return sendWsMessage({
        type: WS_MESSAGE_TYPES.UNSUBSCRIBE,
        channelId
      });
    },
    [sendWsMessage]
  );

  /**
   * Select a channel as active
   * @param {object} channel - Channel to select
   */
  const selectChannel = useCallback(
    async (channel) => {
      setActiveChannel(channel);
      setActiveDmRecipient(null);

      // Subscribe if not already
      if (channel && !subscribedChannels.has(channel.id)) {
        try {
          await subscribeToChannel(channel.id);
        } catch {
          // Subscription error handled in sendWsMessage
        }
      }

      // Clear unread count
      if (channel) {
        setUnreadCounts((prev) => {
          const newMap = new Map(prev);
          newMap.set(channel.id, 0);
          return newMap;
        });
      }
    },
    [subscribedChannels, subscribeToChannel]
  );

  /**
   * Select a DM recipient as active
   * @param {object} recipient - Recipient to select
   */
  const selectDmRecipient = useCallback((recipient) => {
    setActiveChannel(null);
    setActiveDmRecipient(recipient);

    // Clear DM unread count
    if (recipient) {
      const currentId = userId || agentId;
      if (currentId) {
        const conversationKey = getDmConversationKey(currentId, recipient.id);
        setDmUnreadCounts((prev) => {
          const newMap = new Map(prev);
          newMap.set(conversationKey, 0);
          return newMap;
        });
      }
    }
  }, [userId, agentId]);

  // ----------------------------------------
  // Message Actions
  // ----------------------------------------

  /**
   * Fetch message history for a channel
   * @param {string} channelId - Channel ID
   * @param {object} [options] - Fetch options
   * @param {string} [options.before] - Cursor for older messages
   * @param {string} [options.after] - Cursor for newer messages
   * @param {number} [options.limit] - Number of messages to fetch
   */
  const fetchMessages = useCallback(async (channelId, options = {}) => {
    setMessagesLoading(true);

    try {
      const params = new URLSearchParams();
      params.set('limit', (options.limit || MESSAGE_PAGE_SIZE).toString());
      if (options.before) params.set('before', options.before);
      if (options.after) params.set('after', options.after);

      const response = await fetch(
        `/api/channels/${channelId}/messages?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { data: [...], pagination: {...} }
      const rawMessages = data.data || data.messages || data || [];

      // Transform messages to normalize sender info
      // API returns sender as object, but WebSocket gives senderName directly
      const transformedMessages = rawMessages.map(msg => ({
        ...msg,
        // Extract senderName from sender object if not already present
        senderName: msg.senderName ||
          msg.sender?.displayName ||
          msg.sender?.name ||
          msg.sender?.username ||
          'Unknown',
        // Also extract senderId if not present
        senderId: msg.senderId || msg.sender?.id,
        // Extract senderAvatarUrl from sender object
        senderAvatarUrl: msg.senderAvatarUrl || msg.sender?.avatarUrl || null
      }));

      // Sort messages chronologically (oldest first) for display
      // API returns newest first for cursor pagination
      const fetchedMessages = [...transformedMessages].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );

      setMessages((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(channelId) || [];

        if (options.before) {
          // Prepend older messages
          newMap.set(channelId, [...fetchedMessages, ...existing]);
        } else if (options.after) {
          // Append newer messages
          newMap.set(channelId, [...existing, ...fetchedMessages]);
        } else {
          // Replace with fresh data
          newMap.set(channelId, fetchedMessages);
        }

        return newMap;
      });

      setMessagesHasMore((prev) => {
        const newMap = new Map(prev);
        newMap.set(channelId, data.hasMore ?? fetchedMessages.length >= MESSAGE_PAGE_SIZE);
        return newMap;
      });
    } catch {
      // Error fetching messages
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  /**
   * Fetch DM history with a recipient
   * @param {string} recipientId - Recipient ID
   * @param {object} [options] - Fetch options
   */
  const fetchDmMessages = useCallback(async (recipientId, options = {}) => {
    setMessagesLoading(true);

    const currentId = userId || agentId;
    if (!currentId) return;

    const conversationKey = getDmConversationKey(currentId, recipientId);

    try {
      const params = new URLSearchParams();
      params.set('limit', (options.limit || MESSAGE_PAGE_SIZE).toString());
      if (options.before) params.set('before', options.before);
      if (options.after) params.set('after', options.after);

      const response = await fetch(
        `/api/dm/${recipientId}/messages?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch DM messages: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { data: [...], pagination: {...} }
      const rawMessages = data.data || data.messages || data || [];

      // Transform messages to normalize sender info
      // API returns sender as object, but WebSocket gives senderName directly
      const transformedMessages = rawMessages.map(msg => ({
        ...msg,
        // Extract senderName from sender object if not already present
        senderName: msg.senderName ||
          msg.sender?.displayName ||
          msg.sender?.name ||
          msg.sender?.username ||
          'Unknown',
        // Also extract senderId if not present
        senderId: msg.senderId || msg.sender?.id,
        // Extract senderAvatarUrl from sender object
        senderAvatarUrl: msg.senderAvatarUrl || msg.sender?.avatarUrl || null
      }));

      // Sort messages chronologically (oldest first) for display
      // API returns newest first for cursor pagination
      const fetchedMessages = [...transformedMessages].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );

      setMessages((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(conversationKey) || [];

        if (options.before) {
          newMap.set(conversationKey, [...fetchedMessages, ...existing]);
        } else if (options.after) {
          newMap.set(conversationKey, [...existing, ...fetchedMessages]);
        } else {
          newMap.set(conversationKey, fetchedMessages);
        }

        return newMap;
      });

      setMessagesHasMore((prev) => {
        const newMap = new Map(prev);
        newMap.set(conversationKey, data.hasMore ?? fetchedMessages.length >= MESSAGE_PAGE_SIZE);
        return newMap;
      });
    } catch {
      // Error fetching DM messages
    } finally {
      setMessagesLoading(false);
    }
  }, [userId, agentId]);

  /**
   * Upload attachments to the server
   * @param {File[]} files - Files to upload
   * @param {string} [channelId] - Optional channel ID for validation
   * @returns {Promise<object[]>} Uploaded file info with storage paths
   */
  const uploadAttachments = useCallback(async (files, channelId = null) => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();
    files.forEach((fileWrapper) => {
      // fileWrapper can be a File object directly or a wrapper { file, name, ... }
      const file = fileWrapper.file || fileWrapper;
      formData.append('files', file);
    });

    const url = channelId
      ? `/api/attachments/upload?channelId=${channelId}`
      : '/api/attachments/upload';

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to upload attachments');
    }

    const result = await response.json();
    return result.data;
  }, []);

  /**
   * Send a channel message
   * @param {string} channelId - Channel ID
   * @param {string} content - Message content
   * @param {object} [options] - Send options
   * @param {string} [options.replyToId] - Message ID to reply to
   * @param {string[]} [options.mentionedUserIds] - User IDs mentioned
   * @param {string[]} [options.mentionedAgentIds] - Agent IDs mentioned
   * @param {File[]} [options.attachments] - Files to attach
   * @returns {Promise<object>} Send result
   */
  const sendMessage = useCallback(
    async (channelId, content, options = {}) => {
      // Upload attachments first if any
      let uploadedAttachments = [];
      if (options.attachments && options.attachments.length > 0) {
        console.log('[sendMessage] Uploading', options.attachments.length, 'attachments');
        uploadedAttachments = await uploadAttachments(options.attachments, channelId);
        console.log('[sendMessage] Upload result:', uploadedAttachments);
      }

      const attachmentsPayload = uploadedAttachments.map((a) => ({
        originalName: a.originalName,
        fileName: a.fileName,
        storagePath: a.storagePath,
        mimeType: a.mimeType,
        size: a.size
      }));
      console.log('[sendMessage] Sending with attachments:', attachmentsPayload);

      const result = await sendWsMessage({
        type: WS_MESSAGE_TYPES.SEND_MESSAGE,
        channelId,
        content,
        replyToId: options.replyToId || null,
        mentionedUserIds: options.mentionedUserIds || [],
        mentionedAgentIds: options.mentionedAgentIds || [],
        attachments: attachmentsPayload
      });

      // Optimistically add message to local state
      const currentId = userId || agentId;
      if (currentId && result.messageId) {
        const message = {
          id: result.messageId,
          channelId,
          senderId: currentId,
          senderType: userId ? 'user' : 'agent',
          senderName: 'You',
          content,
          replyToId: options.replyToId || null,
          mentionedUserIds: options.mentionedUserIds || [],
          mentionedAgentIds: options.mentionedAgentIds || [],
          attachments: result.attachments || uploadedAttachments,
          createdAt: result.createdAt || new Date().toISOString()
        };

        setMessages((prev) => {
          const newMap = new Map(prev);
          const channelMessages = newMap.get(channelId) || [];

          // Prevent duplicates
          if (!channelMessages.some((m) => m.id === message.id)) {
            newMap.set(channelId, [...channelMessages, message]);
          }

          return newMap;
        });
      }

      return result;
    },
    [sendWsMessage, userId, agentId, uploadAttachments]
  );

  /**
   * Send a direct message
   * @param {string} recipientId - Recipient ID
   * @param {string} content - Message content
   * @param {object} [options] - Send options
   * @param {string} [options.replyToId] - Message ID to reply to
   * @param {File[]} [options.attachments] - Files to attach
   * @returns {Promise<object>} Send result
   */
  const sendDm = useCallback(
    async (recipientId, content, options = {}) => {
      // Upload attachments first if any
      let uploadedAttachments = [];
      if (options.attachments && options.attachments.length > 0) {
        uploadedAttachments = await uploadAttachments(options.attachments);
      }

      const result = await sendWsMessage({
        type: WS_MESSAGE_TYPES.SEND_DM,
        recipientId,
        content,
        replyToId: options.replyToId || null,
        attachments: uploadedAttachments.map((a) => ({
          originalName: a.originalName,
          fileName: a.fileName,
          storagePath: a.storagePath,
          mimeType: a.mimeType,
          size: a.size
        }))
      });

      // Optimistically add message to local state
      const currentId = userId || agentId;
      if (currentId && result.messageId) {
        const conversationKey = getDmConversationKey(currentId, recipientId);

        const message = {
          id: result.messageId,
          senderId: currentId,
          senderType: userId ? 'user' : 'agent',
          senderName: 'You',
          content,
          replyToId: options.replyToId || null,
          isDm: true,
          attachments: result.attachments || uploadedAttachments,
          createdAt: result.createdAt || new Date().toISOString()
        };

        setMessages((prev) => {
          const newMap = new Map(prev);
          const conversationMessages = newMap.get(conversationKey) || [];

          if (!conversationMessages.some((m) => m.id === message.id)) {
            newMap.set(conversationKey, [...conversationMessages, message]);
          }

          return newMap;
        });
      }

      return result;
    },
    [sendWsMessage, userId, agentId, uploadAttachments]
  );

  /**
   * Mark channel messages as read
   * @param {string} channelId - Channel ID
   * @param {string} [messageId] - Specific message ID (marks all if not specified)
   * @returns {Promise<object>} Mark read result
   */
  const markAsRead = useCallback(
    async (channelId, messageId) => {
      return sendWsMessage({
        type: WS_MESSAGE_TYPES.MARK_READ,
        channelId,
        messageId
      });
    },
    [sendWsMessage]
  );

  // ----------------------------------------
  // Computed Values
  // ----------------------------------------

  /**
   * Get messages for the active channel or DM
   */
  const activeMessages = useMemo(() => {
    if (activeChannel) {
      return messages.get(activeChannel.id) || [];
    }
    if (activeDmRecipient) {
      const currentId = userId || agentId;
      if (currentId) {
        const conversationKey = getDmConversationKey(currentId, activeDmRecipient.id);
        return messages.get(conversationKey) || [];
      }
    }
    return [];
  }, [activeChannel, activeDmRecipient, messages, userId, agentId]);

  /**
   * Get total unread count across all channels
   */
  const totalUnreadCount = useMemo(() => {
    let total = 0;
    unreadCounts.forEach((count) => {
      total += count;
    });
    dmUnreadCounts.forEach((count) => {
      total += count;
    });
    return total;
  }, [unreadCounts, dmUnreadCounts]);

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  // Connect WebSocket on mount - intentionally empty deps to prevent reconnection loops
  // Reconnection is handled by ws.onclose handler
  useEffect(() => {
    isComponentMounted.current = true;
    connect();

    return () => {
      isComponentMounted.current = false;
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle visibility change (tab focus, wake from sleep)
  // Force reconnect when tab becomes visible if connection is stale
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if WebSocket is in a bad state
        const ws = wsRef.current;
        const isStale = !ws ||
          ws.readyState === WebSocket.CLOSED ||
          ws.readyState === WebSocket.CLOSING;

        if (isStale) {
          console.log('[ChatContext] Tab became visible with stale connection, reconnecting...');
          // Clear any pending reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          reconnectAttempts.current = 0;
          connect();
        } else if (ws.readyState === WebSocket.OPEN) {
          // Connection looks open, but may be stale after sleep
          // Send a ping and wait for pong to verify
          // If no pong received within timeout, reconnect
          console.log('[ChatContext] Tab became visible, verifying connection...');
          const pongTimeout = setTimeout(() => {
            console.log('[ChatContext] No pong received after visibility change, reconnecting...');
            ws.close();
            reconnectAttempts.current = 0;
            connect();
          }, 5000);

          // Store timeout to clear on pong
          const originalOnMessage = ws.onmessage;
          ws.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if (data.type === 'pong') {
                clearTimeout(pongTimeout);
                console.log('[ChatContext] Connection verified after visibility change');
              }
            } catch {
              // ignore parse errors
            }
            // Call original handler
            if (originalOnMessage) {
              originalOnMessage.call(ws, event);
            }
          };

          // Send ping to verify
          ws.send(JSON.stringify({ type: 'ping' }));

          // Restore original handler after timeout
          setTimeout(() => {
            if (wsRef.current === ws) {
              ws.onmessage = originalOnMessage;
            }
          }, 5100);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect]);

  // Fetch channels, agents, and DM contacts on mount
  useEffect(() => {
    fetchChannels();
    fetchAgents();
    fetchDmContacts();
  }, [fetchChannels, fetchAgents, fetchDmContacts]);

  // Fetch messages when active channel changes - always fetch to catch any missed messages
  useEffect(() => {
    if (activeChannel) {
      const existingMessages = messagesRef.current.get(activeChannel.id) || [];
      if (existingMessages.length > 0) {
        // Fetch messages after the last known message
        const lastMessage = existingMessages[existingMessages.length - 1];
        fetchMessages(activeChannel.id, { after: lastMessage.id });
      } else {
        // No messages yet, fetch initial batch
        fetchMessages(activeChannel.id);
      }
    }
  }, [activeChannel, fetchMessages]);

  // Fetch DM messages when active DM recipient changes - always fetch to catch any missed messages
  useEffect(() => {
    if (activeDmRecipient) {
      const currentId = userId || agentId;
      if (currentId) {
        const conversationKey = getDmConversationKey(currentId, activeDmRecipient.id);
        const existingMessages = messagesRef.current.get(conversationKey) || [];
        if (existingMessages.length > 0) {
          // Fetch messages after the last known message
          const lastMessage = existingMessages[existingMessages.length - 1];
          fetchDmMessages(activeDmRecipient.id, { after: lastMessage.id });
        } else {
          // No messages yet, fetch initial batch
          fetchDmMessages(activeDmRecipient.id);
        }
      }
    }
  }, [activeDmRecipient, fetchDmMessages, userId, agentId]);

  // ----------------------------------------
  // Context Value
  // ----------------------------------------

  const contextValue = useMemo(
    () => ({
      // Connection state
      connectionState,
      connectionError,
      isConnected: connectionState === CONNECTION_STATE.CONNECTED,

      // Channels
      channels,
      channelsLoading,
      channelsError,
      activeChannel,
      subscribedChannels: Array.from(subscribedChannels),

      // DM
      activeDmRecipient,
      dmContacts,
      dmContactsLoading,
      fetchDmContacts,

      // Messages
      messages: activeMessages,
      messagesLoading,
      messagesHasMore: activeChannel
        ? messagesHasMore.get(activeChannel.id) ?? false
        : activeDmRecipient
          ? messagesHasMore.get(
              getDmConversationKey(userId || agentId || '', activeDmRecipient.id)
            ) ?? false
          : false,

      // Unread counts
      unreadCounts: Object.fromEntries(unreadCounts),
      dmUnreadCounts: Object.fromEntries(dmUnreadCounts),
      totalUnreadCount,
      getUnreadCount: (channelId) => unreadCounts.get(channelId) || 0,
      getDmUnreadCount: (recipientId) => {
        const currentId = userId || agentId;
        if (!currentId) return 0;
        const conversationKey = getDmConversationKey(currentId, recipientId);
        return dmUnreadCounts.get(conversationKey) || 0;
      },

      // Agent statuses
      agentStatuses: Object.fromEntries(agentStatuses),
      getAgentStatus: (id) => agentStatuses.get(id) || null,

      // Generating agents
      generatingAgents: Array.from(generatingAgents),
      isAgentGenerating: (id) => generatingAgents.has(id),

      // Agents (for mentions)
      agents,
      fetchAgents,

      // Actions
      fetchChannels,
      createChannel,
      selectChannel,
      selectDmRecipient,
      subscribeToChannel,
      unsubscribeFromChannel,
      fetchMessages,
      fetchDmMessages,
      sendMessage,
      sendDm,
      markAsRead,
      connect,
      disconnect,

      // Channel members
      channelMembers,
      fetchChannelMembers,
      getChannelMembers
    }),
    [
      connectionState,
      connectionError,
      channels,
      channelsLoading,
      channelsError,
      activeChannel,
      subscribedChannels,
      activeDmRecipient,
      dmContacts,
      dmContactsLoading,
      activeMessages,
      messagesLoading,
      messagesHasMore,
      unreadCounts,
      dmUnreadCounts,
      totalUnreadCount,
      agentStatuses,
      generatingAgents,
      agents,
      fetchAgents,
      fetchDmContacts,
      fetchChannels,
      createChannel,
      selectChannel,
      selectDmRecipient,
      subscribeToChannel,
      unsubscribeFromChannel,
      fetchMessages,
      fetchDmMessages,
      sendMessage,
      sendDm,
      markAsRead,
      connect,
      disconnect,
      userId,
      agentId,
      channelMembers,
      fetchChannelMembers,
      getChannelMembers
    ]
  );

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

/**
 * Hook to access chat context
 * @returns {object} Chat context value
 * @throws {Error} If used outside ChatProvider
 */
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

// ============================================
// Exports
// ============================================

export { CONNECTION_STATE, WS_MESSAGE_TYPES };
export default ChatContext;
